import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Skeleton,
  Select,
  MenuItem,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LeadSourceForProject from "./LeadSourceForProject";
import WhatsappTemplateForProject from "./WhatsappTemplateForProject";
import LeadStatusForProject from "./LeadStatusForProject";
import EditIcon from "@mui/icons-material/Edit";
import ProjectTelegram from "./ProjectTelegram";
import CountrySelect from "../../utils/countrySelect";
import CountrySelectForModal from "../../utils/CountrySelectForModal";
import LeadStatusSummary from "./LeadStatusSummary";
import { jwtDecode } from "jwt-decode";
import DefaultAssigneeSection from "./DefaultAssigneeSection";
import { useOrganization } from "../../utils/OrganizationContext";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// Scoped theme for this page's own buttons, fields and dialogs.
// Child sections keep their own styling (their sx wins over the theme).
const pageTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: "#084767", contrastText: "#FFFFFF" },
    error: { main: "#C42B24" },
    text: { primary: INK, secondary: "#4A5A6E" },
    divider: "#EDF1F5",
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT,
    h6: { fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em" },
    body1: { fontSize: 14 },
    body2: { fontSize: 13.5 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, fontFamily: FONT, fontWeight: 600, minHeight: 36, paddingInline: 14 },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        outlined: { borderColor: FIELD_EDGE, background: "#FFFFFF", color: INK, "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" } },
        outlinedError: { borderColor: "#F2CFCA", color: "#A6342A", "&:hover": { background: "#FBEDEB", borderColor: "#F2CFCA" } },
        text: { color: INK_2, "&:hover": { background: "#F4F7FA" } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: "#FFFFFF",
          fontFamily: FONT,
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 13.5,
          borderRadius: 8,
          minHeight: 36,
          "&.Mui-selected, &.Mui-selected:hover": { background: "#EAF2FB", color: "#1B4F82", fontWeight: 700 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 18, boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { gap: 8 } },
    },
    MuiSkeleton: { styleOverrides: { root: { background: "#EDF1F5" } } },
  },
});

const modalLabelStyle = {
  display: "block",
  marginBottom: "6px",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};

const staticFields = [
  { name: "name", label: "Lead Name", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "countryCode", label: "Country Code", type: "text" },
  { name: "isWhatsapp", label: "is on WhatsApp", type: "boolean" },
  { name: "email", label: "Email", type: "email" },
  { name: "alternatePhone", label: "Alternate Phone", type: "text" },
  {
    name: "alternateCountryCode",
    label: "Alternate Country Code",
    type: "text",
  },
  {
    name: "isAlternateWhatsapp",
    label: "is on WhatsApp",
    type: "boolean",
  },

  { name: "leadSource", label: "Lead Source", type: "text" },

  { name: "currentCity", label: "Current City", type: "text" },
  { name: "currentCountry", label: "Current Country", type: "text" },

  { name: "requirement", label: "Requirement", type: "text" },

  {
    name: "preferredPropertyType",
    label: "Preferred Property Type",
    type: "text",
  },

  { name: "minBudget", label: "Minimum Budget", type: "number" },
  { name: "maxBudget", label: "Maximum Budget", type: "number" },

  { name: "timelineToBuy", label: "Timeline To Buy", type: "text" },
  { name: "purpose", label: "Purpose", type: "text" },

  // DATE FIELDS
  { name: "nextFollowUp", label: "Next Follow Up", type: "date" },
  { name: "lastFollowUp", label: "Last Follow Up", type: "date" },
  { name: "remark", label: "Comment", type: "text" },

  // UTM FIELDS
  { name: "utmSource", label: "UTM Source", type: "text" },
  { name: "utmMedium", label: "UTM Medium", type: "text" },
  { name: "utmCampaign", label: "UTM Campaign", type: "text" },
  { name: "utmTerm", label: "UTM Term", type: "text" },
  { name: "utmContent", label: "UTM Content", type: "text" },

  { name: "leadScore", label: "Lead Score", type: "number" },
];

const EXPIRY_OPTIONS = [
  { label: "10 days", value: 10 },
  { label: "20 days", value: 20 },
  { label: "30 days", value: 30 },
  { label: "Never expire", value: -1 },
];

const ViewProject = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [keys, setKeys] = useState([]);
  const [openStructure, setOpenStructure] = useState(false);
  const [inputFields, setInputFields] = useState([]);
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [generatedKey, setGeneratedKey] = useState("");
  const [expiryDays, setExpiryDays] = useState(10);
  const [pendingKeyId, setPendingKeyId] = useState(null);
  const navigate = useNavigate();
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [openKeyModal, setOpenKeyModal] = useState(false);
  const [name, setName] = useState("");
  const isMobile = useMediaQuery("(max-width:750px)");
  const { role } = useOrganization();

  // Meta CAPI Config Modal State
  const [openMetaModal, setOpenMetaModal] = useState(false);
  const [metaPixelIdInput, setMetaPixelIdInput] = useState("");
  const [metaCapiTokenInput, setMetaCapiTokenInput] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  // Confirm popup (replaces window.confirm for disabling a key)
  const [confirmKeyId, setConfirmKeyId] = useState(null);

  const fetchProject = async () => {
    if (!storedUser?.accessToken) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/project?projectId=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );
      setProject(res.data);
      setMetaPixelIdInput(res.data?.metaPixelId || "");
      setMetaCapiTokenInput(res.data?.metaCapiToken || "");
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchProject();
    if (role === "ADMIN") {
      fetchKeys();
    }
  }, [projectId, role]);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/project-key?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setKeys(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const createKey = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/project-key?projectId=${projectId}&exp=${expiryDays}&region=${phoneCountry}&name=${name}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setGeneratedKey(res.data);
      setShowKeyDialog(true);
      fetchKeys();
    } catch (e) {
      console.log(e);
    }
  };

  const handleSaveMetaConfig = async () => {
    try {
      setSavingMeta(true);
      const payload = {
        ...project,
        metaPixelId: metaPixelIdInput,
        metaCapiToken: metaCapiTokenInput,
      };
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project?projectId=${projectId}`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      showModal("Meta CAPI configuration updated successfully!", "success");
      setOpenMetaModal(false);
      fetchProject();
    } catch (error) {
      console.error(error);
      showModal(
        error?.response?.data?.Exception ||
          "Failed to update Meta configuration",
        "error",
      );
    } finally {
      setSavingMeta(false);
    }
  };

  const regenerateKey = async (keyId) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project-key?keyId=${keyId}&exp=${expiryDays}&region=${phoneCountry}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setGeneratedKey(res.data);
      setShowKeyDialog(true);
      fetchKeys();
    } catch (e) {
      console.log(e);
    }
  };

  const handleOpenStructure = async () => {
    setOpenStructure(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead-structure?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setInputFields(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Disable button opens the confirm popup; "Disable" in the popup runs doDisableKey.
  const disableKey = (keyId) => setConfirmKeyId(keyId);

  const doDisableKey = async (keyId) => {
    setConfirmKeyId(null);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project-key?keyId=${keyId}`,
        {},
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      fetchKeys();
    } catch (e) {
      console.log(e);
      showModal("Failed to disable key", "error");
    }
  };

  if (!project) {
    return (
      <ThemeProvider theme={pageTheme}>
        <Box px={0} py={1}>
          <Skeleton variant="rounded" height={44} sx={{ mt: 1, borderRadius: "12px", maxWidth: 320 }} />
          <Skeleton variant="rounded" height={120} sx={{ mt: 2, borderRadius: "14px" }} />
          <Skeleton variant="rounded" height={300} sx={{ mt: 2, borderRadius: "14px" }} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={pageTheme}>
    <div style={{ padding: "4px 0 24px", fontFamily: FONT, color: INK }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ fontFamily: FONT, fontSize: "27px", fontWeight: 800, letterSpacing: "-0.028em", margin: 0 }}>
            Project Details
          </h2>
          {role === "ADMIN" && (
            <Button
              className="btn-contained"
              onClick={() => navigate(`/dashboard/edit-project/${projectId}`)}
              title="Edit project"
              sx={{
                height: "fit-content",
                margin: "0 !important",
                minWidth: "0 !important",
                width: 36,
                minHeight: "36px !important",
                p: "0 !important",
                borderRadius: "10px !important",
                background: "#FFFFFF !important",
                color: `${INK_2} !important`,
                border: `1px solid ${FIELD_EDGE} !important`,
                "&:hover": { background: "#F4F7FA !important" },
              }}
            >
              <EditIcon sx={{ fontSize: 17 }} />
            </Button>
          )}
        </div>
        <Button
          variant="outlined"
          onClick={() => setOpenMetaModal(true)}
          sx={{ height: "fit-content", mb: 0 }}
        >
          Meta CAPI Settings
        </Button>
      </div>

      <Box
        sx={{
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          p: { xs: 1.5, sm: 2 },
          mb: 2,
        }}
      >
        <Grid container spacing={1.25}>
          {Object.entries(project).map(([key, value]) => {
            if (
              value === null ||
              value === "" ||
              key === "id" ||
              key === "telegramChatId" ||
              key === "telegramBotId" ||
              key === "isTelegramAllowed" ||
              key === "assignmentStrategy" ||
              key === "defaultAssignee" ||
              key === "assignmentRoles"
            )
              return null;

            const isDescription = key.toLowerCase() === "description";

            return (
              <Grid size={{ xs: 12, sm: isDescription ? 12 : 4 }} key={key}>
                <Paper
                  elevation={0}
                  style={{
                    padding: "10px 12px",
                    backgroundColor: "#F7F9FC",
                    border: `1px solid #EDF1F5`,
                    borderRadius: 10,
                    height: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <strong
                    style={{
                      display: "block",
                      fontFamily: FONT,
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: MUTED,
                      marginBottom: "4px",
                    }}
                  >
                    {key === "metaPixelId"
                      ? "Meta Pixel ID"
                      : key === "metaCapiToken"
                        ? "Meta CAPI Token"
                        : key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                    :
                  </strong>
                  <span
                    style={{
                      wordBreak: "break-all",
                      fontFamily: key === "metaCapiToken" || key === "metaPixelId" || key.toLowerCase().includes("id") ? MONO : FONT,
                      fontSize: key === "metaCapiToken" || key === "metaPixelId" || key.toLowerCase().includes("id") ? 13 : 14,
                      fontWeight: isDescription ? 500 : 600,
                      lineHeight: 1.5,
                      color: INK,
                    }}
                  >
                    {key === "metaCapiToken"
                      ? value
                        ? `${value.substring(0, 10)}••••••••••••`
                        : "—"
                      : key !== "team"
                        ? value.toString()
                        : project?.team?.name}
                  </span>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <LeadSourceForProject project={project} />
      <WhatsappTemplateForProject project={project} />
      <LeadStatusForProject projectId={projectId} />
      {(role === "ADMIN" || role === "PROJECT_HEAD") && (
        <DefaultAssigneeSection project={project} onUpdated={fetchProject} />
      )}
      {role === "ADMIN" && (
        <Box sx={{ mt: 2 }}>
          <ProjectTelegram
            projectId={projectId}
            telegramBotId={project?.telegramBotId}
            telegramChatId={project?.telegramChatId}
            isTelegramAllowed={project?.isTelegramAllowed}
            onUpdated={fetchProject}
          />
        </Box>
      )}
      <LeadStatusSummary projectId={projectId} project={project} />

      <Button variant="outlined" sx={{ mt: 1 }} onClick={handleOpenStructure}>
        Fields
      </Button>

      {role === "ADMIN" && (
        <Paper
          elevation={0}
          sx={{
            mt: 2.5,
            p: 0,
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "stretch" : "center"}
            gap={1.25}
            mb={0}
            sx={{ px: 2, py: 1.5, borderBottom: "1px solid #EDF1F5" }}
          >
            <Box display="flex" alignItems="center" gap={1.125}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: "#6C5CE7", flexShrink: 0 }} />
              <Typography variant="h6">Project API Keys</Typography>
            </Box>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <Button variant="contained" onClick={() => setOpenKeyModal(true)} sx={{ width: isMobile ? "100%" : "auto" }}>
                Create New Key
              </Button>
            </div>
          </Stack>

          {keys.length === 0 ? (
            <Typography sx={{ px: 2, py: 2.25, fontSize: 13.5, color: MUTED }}>No API keys created yet.</Typography>
          ) : (
            <Grid container spacing={0}>
              {keys.map((key) => (
                <Grid key={key.id} size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{ p: "12px 16px", borderRadius: 0, borderBottom: "1px solid #F1F4F8", background: "transparent" }}
                  >
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      justifyContent="space-between"
                      alignItems={isMobile ? "stretch" : "center"}
                      gap={1.25}
                    >
                      <div style={{ width: isMobile ? "100%" : "80%", minWidth: 0 }}>
                        <Typography fontWeight={700} sx={{ fontSize: 14, color: key.isEnable ? INK : MUTED }}>
                          {key.name || "Unnamed Key"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5, mt: 0.25 }}>
                          Region: <span style={{ fontFamily: MONO }}>{key.region || "—"}</span> &nbsp;|&nbsp; Expires:{" "}
                          <span style={{ fontFamily: MONO }}>{key.expiry || "Never"}</span>
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontFamily: MONO, fontSize: 13, letterSpacing: "0.08em", color: "#8A97A8", mt: 0.25 }}>
                          •••••••••••••••••••••
                        </Typography>
                      </div>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => disableKey(key.id)}
                        disabled={!key.isEnable}
                        sx={{ flexShrink: 0, "&.Mui-disabled": { background: "#F1F4F8", borderColor: LINE, color: MUTED } }}
                      >
                        {key.isEnable ? "Disable" : "Disabled"}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Meta CAPI Settings Dialog Modal */}
      <Dialog
        open={openMetaModal}
        onClose={() => setOpenMetaModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Project-Level Meta CAPI Configuration
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 2, mt: 0.5, lineHeight: 1.55, textWrap: "pretty" }}
          >
            Configure the project-specific Meta Pixel Dataset ID and Conversions
            API Access Token. These credentials handle server-side tracking
            securely for this specific real estate project.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div>
              <label style={modalLabelStyle}>
                Meta Pixel / Dataset ID
              </label>
              <TextField
                fullWidth
                placeholder="e.g., 1293094046180159"
                value={metaPixelIdInput}
                onChange={(e) => setMetaPixelIdInput(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { height: 42 }, "& input": { fontFamily: MONO, fontSize: 13 } }}
              />
            </div>
            <div>
              <label style={modalLabelStyle}>
                Meta CAPI Access Token
              </label>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Paste project-specific CAPI Token..."
                value={metaCapiTokenInput}
                onChange={(e) => setMetaCapiTokenInput(e.target.value)}
                sx={{ "& textarea": { fontFamily: MONO, fontSize: 12.5, lineHeight: 1.5 } }}
              />
            </div>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenMetaModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMetaConfig}
            disabled={savingMeta}
          >
            {savingMeta ? "Saving..." : "Save Configuration"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showKeyDialog}
        onClose={() => setShowKeyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New API Key Generated</DialogTitle>

        <DialogContent>
          <Typography
            color="error"
            sx={{
              mb: 1,
              p: 1.25,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.5,
              color: "#A6342A",
              background: "#FBEDEB",
              border: "1px solid #F2CFCA",
              borderRadius: "10px",
            }}
          >
            If you lose or forgot your access key, you cannot retrieve it.
            Instead, regenerate a new access key.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 1,
              backgroundColor: "#F7F9FC",
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              wordBreak: "break-all",
              fontFamily: MONO,
              fontSize: 13,
              color: INK,
            }}
          >
            {generatedKey}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(generatedKey);
            }}
          >
            Copy
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setShowKeyDialog(false);
              setGeneratedKey("");
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openStructure}
        onClose={() => setOpenStructure(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lead Structure</DialogTitle>
        <DialogContent>
          {inputFields?.fieldNames?.length > 0 ||
          inputFields?.customFields?.length > 0 ? (
            <Paper elevation={0} sx={{ p: 2, backgroundColor: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: "10px" }}>
              <pre style={{ margin: 0, fontFamily: MONO, fontSize: 12.5, lineHeight: 1.6, color: INK, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(
                  {
                    ...(inputFields?.fieldNames?.reduce((acc, field) => {
                      acc[field] = "";
                      return acc;
                    }, {}) || {}),

                    ...(inputFields?.customFields?.reduce((acc, field) => {
                      acc[field.fieldName] = {
                        type: field.type,
                        value: field.type === "MULTI_SELECT" ? [] : "",
                        options:
                          field.type === "SINGLE_SELECT" ||
                          field.type === "MULTI_SELECT"
                            ? field.options?.map((opt) => ({
                                id: opt.optionId,
                                value: opt.value,
                              }))
                            : undefined,
                      };
                      return acc;
                    }, {}) || {}),
                  },
                  null,
                  2,
                )}
              </pre>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ p: 2, backgroundColor: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: "10px" }}>
              <pre style={{ margin: 0, fontFamily: MONO, fontSize: 12.5, lineHeight: 1.6, color: INK, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(
                  staticFields.reduce((acc, field) => {
                    acc[field.name] = "";
                    return acc;
                  }, {}),
                  null,
                  2,
                )}
              </pre>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setOpenStructure(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openKeyModal}
        onClose={() => setOpenKeyModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
          },
        }}
      >
        <DialogTitle>Create New API Key</DialogTitle>

        <DialogContent
          sx={{
            overflow: "visible",
            pt: 2,
          }}
        >
          <Stack spacing={2.25}>
            <div>
              <label style={modalLabelStyle}>
                Key Name
              </label>

              <input
                placeholder="e.g. Facebook Ads Key"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: 42,
                  padding: "0 12px",
                  margin: 0,
                  borderRadius: "10px",
                  border: `1px solid ${FIELD_EDGE}`,
                  background: "#FFFFFF",
                  fontFamily: FONT,
                  fontSize: 14,
                  color: INK,
                  outline: "none",
                }}
              />
            </div>

            <Grid container spacing={2}>
              {/* Region */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <label style={modalLabelStyle}>
                  Default Region
                </label>

                <div>
                  <CountrySelectForModal
                    value={phoneCountry}
                    onChange={(c) => setPhoneCountry(c)}
                  />
                </div>
              </Grid>

              {/* Expiry */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <label style={modalLabelStyle}>
                  Expiry
                </label>

                <ThemedSelect
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  options={EXPIRY_OPTIONS}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenKeyModal(false)}>Cancel</Button>

          <Button
            variant="contained"
            onClick={async () => {
              await createKey();
              setOpenKeyModal(false);
              setName("");
            }}
          >
            Generate Key
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />

      {/* Disable-key confirmation (replaces window.confirm) */}
      <Dialog
        open={!!confirmKeyId}
        onClose={() => setConfirmKeyId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", p: "24px 22px 20px", textAlign: "center" } }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
          Disable this key?
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 14, color: "#4A5A6E", mt: 0.75, mb: 2.5, textWrap: "pretty" }}>
          Are you sure you want to disable this key? It cannot be used to add leads after this.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Button variant="outlined" fullWidth onClick={() => setConfirmKeyId(null)} sx={{ height: 42 }}>
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={() => doDisableKey(confirmKeyId)}
            sx={{
              height: 42,
              background: "#C42B24",
              color: "#FFFFFF",
              "&:hover": { background: "#A6342A" },
            }}
          >
            Disable
          </Button>
        </Box>
      </Dialog>
    </div>
    </ThemeProvider>
  );
};

export default ViewProject;